import React from 'react';

/*
 * HUD ornaments for the plan 17 style directions: small inline SVG drawn in
 * currentColor or the --tome-* tokens, no filters, decorative only. The HUD
 * does not import tome components (plan 17 §2.1), so these are its own,
 * lighter drawings in the tome's language.
 */

type OrnProps = { className?: string; at?: string };

/** A short creeping vine for a frame corner; mirrored per corner by CSS (`[data-at]`). */
export const HudVine: React.FC<OrnProps> = ({ className = '', at = 'tl' }) => (
  <svg className={`hudt-orn hudt-vine ${className}`} data-at={at} viewBox="0 0 56 56" fill="none" aria-hidden="true">
    <path d="M-2 30 Q4 13 25 8 Q40 5 54 3" stroke="var(--hudt-vine-dark, #162e1c)" strokeWidth="2.6" strokeLinecap="round" />
    <path d="M-2 30 Q4 13 25 8 Q40 5 54 3" stroke="var(--hudt-vine, #3d6b41)" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M20 10 Q27 1 34 6 Q36 10 31 13" stroke="var(--hudt-vine, #558842)" strokeWidth="1" strokeLinecap="round" />
    <path d="M12 12 C8 4 18 2 20 9 C18 14 13 14 12 12 Z" fill="var(--hudt-leaf, #2c5a32)" />
    <path d="M38 6 C35 -1 45 -2 46 4 C44 8 40 8 38 6 Z" fill="var(--hudt-leaf-hi, #3c7444)" />
    <circle cx="46" cy="4.5" r="1.4" fill="var(--tome-gilt-bright, #e5c069)" opacity="0.9" />
    <path d="M2 24 C-3 19 2 14 6 17 C7 21 5 24 2 24 Z" fill="var(--hudt-leaf, #2c5a32)" />
  </svg>
);

/** A branch with leaves, laid along the left edge of a tall piece (direction C's tracker). */
export const HudBranch: React.FC<OrnProps> = ({ className = '' }) => (
  <svg className={`hudt-orn hudt-branch ${className}`} viewBox="0 0 18 200" preserveAspectRatio="none" fill="none" aria-hidden="true">
    <path d="M9 0 C3 30 14 55 8 85 C3 115 13 140 8 170 C6 185 9 194 8 200" stroke="var(--hudt-vine-dark, #162e1c)" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M9 0 C3 30 14 55 8 85 C3 115 13 140 8 170 C6 185 9 194 8 200" stroke="var(--hudt-vine, #3d6b41)" strokeWidth="1" strokeLinecap="round" />
    <path d="M9 34 C16 28 18 36 13 40 Z" fill="var(--hudt-leaf-hi, #3c7444)" />
    <path d="M7 70 C0 64 -1 73 4 76 Z" fill="var(--hudt-leaf, #2c5a32)" />
    <path d="M10 118 C17 112 19 120 14 124 Z" fill="var(--hudt-leaf-hi, #3c7444)" />
    <path d="M7 156 C0 150 -1 159 4 162 Z" fill="var(--hudt-leaf, #2c5a32)" />
  </svg>
);

/** A hairline with a leaf sprig at its centre: separators between tracker entries. */
export const HudSprigRule: React.FC<OrnProps> = ({ className = '' }) => (
  <div className={`hudt-sprig-rule ${className}`} aria-hidden="true">
    <span />
    <svg viewBox="0 0 28 10" fill="none">
      <path d="M2 5 H26" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      <path d="M14 5 C10 0 6 1 5 4 C8 6 11 6 14 5 Z" fill="currentColor" opacity="0.85" />
      <path d="M14 5 C18 10 22 9 23 6 C20 4 17 4 14 5 Z" fill="currentColor" opacity="0.65" />
    </svg>
    <span />
  </div>
);

/** Small glyphs drawn rather than typed (the vendored fonts carry narrow ranges). */
export const HudGlyph: React.FC<{
  kind: 'hp' | 'mp' | 'xp' | 'storm' | 'blades' | 'pin' | 'close' | 'quest' | 'ready';
}> = ({ kind }) => {
  switch (kind) {
  case 'hp':
    return (
      <svg className="hudt-glyph" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M6 10.5 C2 7.5 1 5.5 1.5 3.8 C2 2 4.6 1.6 6 3.6 C7.4 1.6 10 2 10.5 3.8 C11 5.5 10 7.5 6 10.5 Z" fill="currentColor" />
      </svg>
    );
  case 'mp':
    return (
      <svg className="hudt-glyph" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M6 1 C8.5 4.5 10 6.3 10 8 A4 4 0 0 1 2 8 C2 6.3 3.5 4.5 6 1 Z" fill="currentColor" />
      </svg>
    );
  case 'xp':
    return (
      <svg className="hudt-glyph" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M6 1 L7.3 4.6 L11 4.7 L8 7 L9.1 10.7 L6 8.5 L2.9 10.7 L4 7 L1 4.7 L4.7 4.6 Z" fill="currentColor" />
      </svg>
    );
  case 'storm':
    return (
      <svg className="hudt-glyph" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M7 0.5 L2.5 6.8 H5.6 L4.6 11.5 L9.5 4.8 H6.3 Z" fill="currentColor" />
      </svg>
    );
  case 'blades':
    return (
      <svg className="hudt-glyph" viewBox="0 0 10 10" aria-hidden="true">
        <path d="M1 1 L8 8 M2 9 L9 2 M6.5 9.5 L9.5 6.5 M0.5 3.5 L3.5 0.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  case 'pin':
    return (
      <svg className="hudt-glyph" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M6 11 C3.5 8 2.5 6.3 2.5 4.6 A3.5 3.5 0 0 1 9.5 4.6 C9.5 6.3 8.5 8 6 11 Z" fill="none" stroke="currentColor" strokeWidth="1.2" />
        <circle cx="6" cy="4.6" r="1.3" fill="currentColor" />
      </svg>
    );
  case 'close':
    return (
      <svg className="hudt-glyph" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M3 3 L9 9 M9 3 L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  case 'quest':
    return (
      <svg className="hudt-glyph" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2 1.5 H8.5 A1.5 1.5 0 0 1 10 3 V10.5 H3.5 A1.5 1.5 0 0 1 2 9 Z M4 4 H8 M4 6 H8 M4 8 H6.5" fill="none" stroke="currentColor" strokeWidth="1" />
      </svg>
    );
  case 'ready':
    return (
      <svg className="hudt-glyph" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2 6.5 L5 9.2 L10 3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
};
